import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SynonymRollbackTarget,
  SynonymSaveResponse,
  UserDictionaryValidateResponse,
  searchDictionaryApi,
} from '../../../lib/api/searchDictionary';
import { searchDictionaryQuery } from '../queries';

export type SaveSynonymVariables = {
  /** 없으면 새 규칙 */
  ruleId?: string;
  terms: string[];
  /** 수정일 때 편집을 시작하며 본 토큰 */
  previousTerms?: string[];
  confirmWarnings: boolean;
};

/**
 * 저장 뒤 캐시 정리.
 * 동의어는 저장 즉시 고객 검색에 반영되고, 겹침 경고는 **다른 줄**의 표시까지 바꾼다 — 목록은 통째로 다시 읽는다.
 * 이력은 큐를 거쳐 쌓여 즉시 읽으면 비어 있을 수 있다. 무효화만 해 두고 탭을 열 때 다시 읽는다.
 */
const useInvalidateAfterSave = () => {
  const queryClient = useQueryClient();
  return (ruleId: string | undefined, removed: boolean): void => {
    void queryClient.invalidateQueries({ queryKey: searchDictionaryQuery.synonyms._def });
    void queryClient.invalidateQueries({ queryKey: searchDictionaryQuery.validation._def });
    void queryClient.invalidateQueries({ queryKey: searchDictionaryQuery.preview._def });
    void queryClient.invalidateQueries({ queryKey: searchDictionaryQuery.logs._def });
    if (!ruleId) return;
    // 지운 줄의 상세는 다시 읽으면 404 다 — 무효화 대신 지운다(프로모션 삭제와 같은 이유)
    if (removed) queryClient.removeQueries({ queryKey: searchDictionaryQuery.synonym(ruleId).queryKey });
    else void queryClient.invalidateQueries({ queryKey: searchDictionaryQuery.synonym(ruleId).queryKey });
  };
};

export const useSaveSynonymMutation = () => {
  const invalidate = useInvalidateAfterSave();
  return useMutation<SynonymSaveResponse, Error, SaveSynonymVariables>({
    mutationFn: ({ ruleId, terms, previousTerms, confirmWarnings }) =>
      ruleId
        ? searchDictionaryApi.update(ruleId, { terms, previousTerms: previousTerms ?? [], confirmWarnings })
        : searchDictionaryApi.create({ terms, confirmWarnings }),
    onSuccess: (response, { ruleId }) => invalidate(ruleId ?? response.rule?.id, false),
  });
};

export const useDeleteSynonymMutation = () => {
  const invalidate = useInvalidateAfterSave();
  return useMutation<SynonymSaveResponse, Error, { ruleId: string; previousTerms: string[] }>({
    mutationFn: ({ ruleId, previousTerms }) => searchDictionaryApi.remove(ruleId, { previousTerms }),
    onSuccess: (_, { ruleId }) => invalidate(ruleId, true),
  });
};

export const useRollbackSynonymMutation = () => {
  const invalidate = useInvalidateAfterSave();
  return useMutation<SynonymSaveResponse, Error, { logId: string; ruleId: string; to: SynonymRollbackTarget }>({
    mutationFn: ({ logId, to }) => searchDictionaryApi.rollback(logId, to),
    onSuccess: (response, { ruleId }) => invalidate(ruleId, response.rule === null),
  });
};

export const useValidateUserDictionaryMutation = () =>
  useMutation<UserDictionaryValidateResponse, Error, string[]>({
    mutationFn: (lines) => searchDictionaryApi.validateUserDictionary({ lines }),
  });
